import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, AlertTriangle } from 'lucide-react';

import './DropdownField.css';

/**
 * Dropdowns for Behold CRM. Two flavors:
 *
 *   <SingleSelect>  — one value, picker
 *   <MultiSelect>   — multiple values, with chips
 *
 * Both follow the same rules:
 *   - Options come from settings (passed as the `options` prop)
 *   - Strict-but-tolerant: existing values that aren't in `options` show with
 *     a warning chip (▲) but stay until the user removes them
 *   - Empty value is allowed (renders placeholder text)
 *
 * Storage format:
 *   - SingleSelect: a plain string (or null/'')
 *   - MultiSelect: comma-separated string ("Equity, Debt") for backward
 *     compatibility with the existing TEXT columns. We split on `, ` for
 *     parsing and join on `, ` for storage.
 */

// ============================================================================
// Helpers — string ↔ array conversion for multi-select
// ============================================================================

export function parseMulti(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value.split(/,\s*/).map(t => t.trim()).filter(Boolean);
}

export function stringifyMulti(arr) {
    if (!arr || arr.length === 0) return null;
    return arr.join(', ');
}

// ============================================================================
// Common: outside-click handler
// ============================================================================

function useOutsideClick(ref, handler) {
    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) handler();
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [ref, handler]);
}

// ============================================================================
// SingleSelect
// ============================================================================

export function SingleSelect({ value, options = [], onChange, placeholder = 'Select…', disabled = false, allowClear = true }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    useOutsideClick(ref, () => setOpen(false));

    // Is the current value out-of-options? (allowed, but flagged)
    const isUnknown = value && !options.some(o => o === value);

    const filtered = options.filter(o =>
        !search || o.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (option) => {
        onChange(option);
        setOpen(false);
        setSearch('');
    };

    return (
        <div className={`dropdown${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`} ref={ref}>
            <button
                type="button"
                className="dropdown-trigger"
                onClick={() => !disabled && setOpen(!open)}
                disabled={disabled}
            >
                {value ? (
                    <span className={`dropdown-value${isUnknown ? ' is-unknown' : ''}`}>
                        {isUnknown && <AlertTriangle size={11} strokeWidth={2}/>}
                        {value}
                    </span>
                ) : (
                    <span className="dropdown-placeholder">{placeholder}</span>
                )}
                <ChevronDown size={14} strokeWidth={2} className="dropdown-caret"/>
            </button>

            {open && (
                <div className="dropdown-menu">
                    {options.length > 6 && (
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="dropdown-search"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    <ul className="dropdown-list">
                        {allowClear && value && (
                            <li className="dropdown-item dropdown-item-clear"
                                onClick={() => handleSelect(null)}>
                                <X size={11} strokeWidth={2}/> Clear selection
                            </li>
                        )}
                        {filtered.length === 0 ? (
                            <li className="dropdown-item dropdown-item-empty">
                                No matches. Add this value in <em>Settings → Dropdowns</em>.
                            </li>
                        ) : (
                            filtered.map(option => (
                                <li
                                    key={option}
                                    className={`dropdown-item${value === option ? ' is-selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {value === option && <Check size={12} strokeWidth={2.5}/>}
                                    <span>{option}</span>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// MultiSelect — chips display + dropdown to add
// ============================================================================

export function MultiSelect({ value, options = [], onChange, placeholder = 'Select…', disabled = false }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    useOutsideClick(ref, () => setOpen(false));

    // Parse value into array (handles both string and array input)
    const selected = parseMulti(value);

    // Tag selected values that are out-of-options
    const isUnknown = (val) => !options.some(o => o === val);

    const filtered = options.filter(o => {
        if (selected.includes(o)) return false;            // hide already-picked
        if (!search) return true;
        return o.toLowerCase().includes(search.toLowerCase());
    });

    const handleAdd = (option) => {
        const next = [...selected, option];
        onChange(stringifyMulti(next));
        setSearch('');
    };

    const handleRemove = (option) => {
        const next = selected.filter(s => s !== option);
        onChange(stringifyMulti(next));
    };

    return (
        <div className={`dropdown dropdown-multi${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`} ref={ref}>
            <div className="dropdown-trigger dropdown-multi-trigger" onClick={() => !disabled && setOpen(true)}>
                {selected.length === 0 ? (
                    <span className="dropdown-placeholder">{placeholder}</span>
                ) : (
                    <div className="dropdown-chips">
                        {selected.map(val => (
                            <span
                                key={val}
                                className={`dropdown-chip${isUnknown(val) ? ' is-unknown' : ''}`}
                            >
                                {isUnknown(val) && <AlertTriangle size={10} strokeWidth={2}/>}
                                {val}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleRemove(val); }}
                                    className="dropdown-chip-remove"
                                    disabled={disabled}
                                >
                                    <X size={10} strokeWidth={2.5}/>
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <ChevronDown size={14} strokeWidth={2} className="dropdown-caret"/>
            </div>

            {open && (
                <div className="dropdown-menu">
                    {options.length > 6 && (
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="dropdown-search"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    <ul className="dropdown-list">
                        {filtered.length === 0 ? (
                            <li className="dropdown-item dropdown-item-empty">
                                {selected.length === options.length
                                    ? 'All options selected.'
                                    : <>No matches. Add values in <em>Settings → Dropdowns</em>.</>}
                            </li>
                        ) : (
                            filtered.map(option => (
                                <li
                                    key={option}
                                    className="dropdown-item"
                                    onClick={() => handleAdd(option)}
                                >
                                    <span>{option}</span>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
